import React, { useEffect, useMemo, useRef, useState } from "react";
import { GetStoreItemsParams } from "@/api/interfaces/items.interface";
import { GetStoreItems } from "@/api/items";
import { GetStoreBankAccount } from "@/api/stores";
import { useFetcher } from "@/api/utils";
import { ProductItemInterface } from "@/assets/interfaces";
import { checkStoreIsOpen, computeItemAvailability, getStoreFrontSEOTags } from "@/assets/js/utils/utils";
import useAnalytics, { PAGE_TYPE } from "@/components/hooks/analytics";
import { useModals } from "@/components/hooks/useModals";
import usePagination from "@/components/hooks/usePagination";
import useSearchParams from "@/components/hooks/useSearchParams";
import Portal from "@/components/portal";
import SEOTags from "@/components/seo-tags";
import { StoreFrontErrors } from "@/components/storefronts-v1";
import ItemsGridView from "@/components/storefronts-v2/sections/items-grid";
import ProductDetailsModal from "@/components/storefronts-v2/modals/product-details";
import StoreFooter from "@/components/storefronts-v2/sections/footer";
import CartContext from "@/contexts/cart-context";
import StoreMaintenanceMode from "@/components/storefronts-v1/ui/store-maintenance-mode";
import SlugDisabledMode from "@/components/storefronts-v1/ui/slug-disabled-mode";
import { Header, Cart } from "@/components/storefronts-v2/sections";
import FloatingCartAction from "@/components/storefronts-v2/ui/floating-cart-action";
import SwitchCategories from "@/components/storefronts-v2/ui/switch-categories";
import { StoreFrontProps } from ".";
import useStorefrontsInitializer from "../hooks/useStorefrontsInitializer";
import CartContextV2 from "@/contexts/cart-context-v2";

type ModalKeys =
  | "about_store"
  | "delivery_fees"
  | "refund_policy"
  | "more_info"
  | "image_details"
  | "explainer"
  | "product_details"
  | "search_modal";

const StorefrontProductsMain: React.FC<StoreFrontProps> = (props) => {
  const { error, data, notFound, disabled_slug, slug, query, botSession } = props;
  const PER_PAGE = 10;
  const { currentPage, goNext: handleNext, setPage } = usePagination();
  const store = data?.store;

  const {
    fullySetup,
    modals,
    toggleModal,
    saveAnalytics,
    fetchStoreBankAccountReq,
    openItem,
    selectedItem,
    maintenanceMode,
    storeIsOpen,
    openItemFromModal,
  } = useStorefrontsInitializer(store, query);
  const [viewMode, setViewMode] = useState(store?.configuration.view_modes.default);
  const { category, search } = useSearchParams(["category", "search"]);

  const [items, setItems] = useState<ProductItemInterface[]>([]);
  const [pagData, setPagData] = useState({ next_page: null, prev_page: null, total_pages: null });

  const formattedItems = useMemo(() => {
    return items.map((i) => computeItemAvailability(i));
  }, [items]);

  const getFilter = useMemo(() => {
    if (error || notFound) {
      return { store: null };
    }

    const filter = {
      store: store?.id,
    };

    if (category || data?.category) {
      let computedCategory = category || data.category;
      if (computedCategory !== "all") {
        filter["category"] = computedCategory;
      }
    }

    if (search || data?.search) {
      filter["search"] = search || data?.search;
    }

    return filter;
  }, [category, search]);

  const {
    response,
    error: itemsFetchError,
    isLoading,
    makeRequest,
  } = useFetcher<GetStoreItemsParams>(
    GetStoreItems,
    {
      filter: getFilter,
      page: currentPage,
      per_page: PER_PAGE,
      separateFeaturedItems: false,
    },
    [store?.id]
  );

  const computedLoadingState = isLoading || (response?.data && formattedItems.length < 1);

  useEffect(() => {
    resetPage();
  }, [search, category]); //might be problematic

  useEffect(() => {
    if (response?.data) {
      const filteredData =
        formattedItems?.length > 0
          ? [...response?.data?.items?.other_items].filter(
              (i) => formattedItems.findIndex((item) => item.id === i.id) === -1
            )
          : [...(response?.data?.items?.other_items ?? [])];

      const itemsToSave = response?.page === 1 ? [...filteredData] : [...formattedItems, ...filteredData];

      setPagData({
        next_page: response?.next_page,
        prev_page: response?.prev_page,
        total_pages: response?.total_pages,
      });
      setItems(itemsToSave);
      data.category = null;
      data.search = null;
    }
  }, [response]);

  const goNext = () => {
    if (currentPage !== items.length / PER_PAGE) return;

    handleNext();
  };

  const resetPage = () => {
    setPage(1);
    setItems([]);
  };

  if (notFound || error || !store) {
    return <StoreFrontErrors {...{ notFound, slug, error, store: store }} />;
  }

  if (disabled_slug) {
    return (
      <>
        <SEOTags tags={getStoreFrontSEOTags(slug, store)} storeLogo={store?.logo} />
        <SlugDisabledMode store={store} />
      </>
    );
  }

  if (maintenanceMode) {
    return (
      <>
        <SEOTags tags={getStoreFrontSEOTags(slug, store)} storeLogo={store?.logo} />
        <StoreMaintenanceMode store={store} />
      </>
    );
  }

  return (
    <>
      <SEOTags tags={getStoreFrontSEOTags(slug, store)} storeLogo={store?.logo} />
      <CartContextV2.Provider
        initialState={{
          storeId: store.id,
          cartRef: store.id,
          account: fetchStoreBankAccountReq?.response?.data,
          currencySettings: store?.currencies
            ? {
                active: store?.currencies?.storefront_default,
                rates: store?.currencies?.rates,
                options: store?.currencies?.storefront,
                defaultCurrency: store?.currencies?.products,
                allRates: store["meta"]?.["rates"]?.rates ?? {},
                rateId: store["meta"]?.["rates"]?.id ?? "",
                markups: store?.currencies?.rates,
              }
            : undefined,
        }}
      >
        {fullySetup && (
          <div>
            <Header
              resetPage={resetPage}
              store={store}
              mode="light"
              showWelcomeMessage={false}
              toggleSearch={() => toggleModal("search_modal")}
              modals={modals}
              toggle={(key: ModalKeys) => toggleModal(key)}
              openItem={openItem}
            />

            <main className={`w-full px-5 sm:px-9 md:px-12.5 py-7.5 sm:py-10 !pb-[50px]`}>
              <div className="store-container">
                <div className="flex justify-between items-center  mb-5 sm:mb-4.5 lg:mb-5">
                  <h3 className="text-black text-1sm sm:text-base font-bold flex items-center font-display">
                    Products
                  </h3>
                  <SwitchCategories categories={data.store.categories} itemsLoading={isLoading} resetPage={resetPage} />
                </div>

                <div>
                  <ItemsGridView
                    resetPage={resetPage}
                    productsLoading={isLoading}
                    showFeatured={false}
                    search={search || data?.search}
                    {...{
                      goNext,
                      currentPage,
                      pagData,
                      items: { main: formattedItems },
                      setSelectedItem: openItem,
                      isLoading: computedLoadingState,
                    }}
                  />
                </div>

                <Portal>
                  {selectedItem && (
                    <ProductDetailsModal
                      show={modals.product_details}
                      toggle={() => toggleModal("product_details")}
                      item={selectedItem}
                      setSelectedItem={openItemFromModal}
                    />
                  )}
                </Portal>

                <Cart storeIsOpen={storeIsOpen.open} phoneNumber={data.store.phone} store={store} />
                <FloatingCartAction productShown={modals.product_details} />
              </div>
            </main>

            <StoreFooter store={store} toggle={(key: ModalKeys) => toggleModal(key)} />
          </div>
        )}

        {!fullySetup && (
          <div className="relative h-screen overflow-hidden flex flex-col items-center justify-center">
            <div className="spinner spinner--md text-black-secondary"></div>
          </div>
        )}
      </CartContextV2.Provider>
    </>
  );
};

export default StorefrontProductsMain;
